// src/Login.jsx
import React, { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { signInWithGoogle } from "./googleAuth";
import porquinho from "./porquinho.png";
import porquinhoCarro from "./porquinho-carro.png";

// Botão "Entrar com Google" temporariamente desativado (visualmente) enquanto
// investigamos com o suporte do Supabase o erro "OAuth state parameter missing"
// (callback duplicado). O código de login com Google continua todo aqui,
// pronto pra ser reativado — é só trocar para true.
const GOOGLE_LOGIN_ENABLED = true;

// Biometria via Capacitor (só no Android/iOS nativo)
// Usa window.Capacitor para evitar que o webpack tente resolver o módulo no build web
function isCapacitorNativo() {
  return typeof window !== "undefined" && window.Capacitor && window.Capacitor.isNativePlatform();
}

async function isBiometriaDisponivel() {
  if (!isCapacitorNativo()) return false;
  try {
    const { NativeBiometric } = window.Capacitor.Plugins;
    if (!NativeBiometric) return false;
    const result = await NativeBiometric.isAvailable();
    return result.isAvailable === true;
  } catch { return false; }
}

async function autenticarBiometria() {
  if (!isCapacitorNativo()) return false;
  try {
    const { NativeBiometric } = window.Capacitor.Plugins;
    if (!NativeBiometric) return false;
    await NativeBiometric.verifyIdentity({
      reason: "Acesse o Mapa Financeiro 5G",
      title: "Biometria",
      subtitle: "Use sua digital para entrar",
      description: "Autentique para continuar",
    });
    return true;
  } catch { return false; }
}

export default function Login({ onCadastroSucesso }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [nome, setNome] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [celular, setCelular] = useState("");
  const [biometriaDisponivel, setBiometriaDisponivel] = useState(false);
  const [biometriaEmail, setBiometriaEmail] = useState("");

  useEffect(() => {
    // Verificar se biometria está disponível
    isBiometriaDisponivel().then(ok => {
      if (ok) {
        setBiometriaDisponivel(true);
        // Recuperar e-mail salvo para biometria
        const emailSalvo = localStorage.getItem("mf5g_bio_email");
        if (emailSalvo) setBiometriaEmail(emailSalvo);
      }
    });
  }, []);

  async function handleLogin(e) {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password.trim()) { setError("Preencha o e-mail e a senha."); return; }
    setLoading(true);
    try {
      const { error: authError } = await supabase.auth.signInWithPassword({ email: email.trim(), password: password.trim() });
      if (authError) {
        if (authError.message.includes("Invalid login credentials")) setError("E-mail ou senha incorretos. Tente novamente.");
        else if (authError.message.includes("Email not confirmed")) setError("E-mail ainda não confirmado. Verifique sua caixa de entrada.");
        else setError("Erro ao entrar: " + authError.message);
      } else {
        // Salvar credenciais no keystore seguro do Android para biometria futura
        if (biometriaDisponivel) {
          try {
            const { NativeBiometric } = window.Capacitor.Plugins;
            await NativeBiometric.setCredentials({
              username: email.trim(),
              password: password.trim(),
              server: "mapafinanceiro5g.com.br",
            });
            localStorage.setItem("mf5g_bio_email", email.trim());
          } catch { /* silencioso — biometria opcional */ }
        }
      }
    } catch (err) {
      setError("Erro inesperado. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }

  async function handleBiometria() {
    if (!biometriaEmail) {
      setError("Faça login com e-mail e senha uma vez para ativar a biometria.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      // 1. Verificar se já tem sessão ativa
      const { data: { session } } = await supabase.auth.getSession();
      if (session) return; // já logado, não precisa fazer nada

      // 2. Pedir autenticação biométrica
      const ok = await autenticarBiometria();
      if (!ok) { setError("Biometria não reconhecida. Tente novamente."); setLoading(false); return; }

      // 3. Recuperar credenciais salvas no keystore seguro
      const { NativeBiometric } = window.Capacitor.Plugins;
      let credentials = null;
      try {
        credentials = await NativeBiometric.getCredentials({ server: "mapafinanceiro5g.com.br" });
      } catch { credentials = null; }

      if (!credentials?.username || !credentials?.password) {
        setError("Credenciais não encontradas. Faça login com e-mail e senha uma vez para reativar a biometria.");
        setLoading(false);
        return;
      }

      // 4. Fazer login real no Supabase com as credenciais recuperadas
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: credentials.username,
        password: credentials.password,
      });

      if (authError) {
        setError("Não foi possível entrar com biometria. Faça login com e-mail e senha.");
      }
    } catch {
      setError("Erro na biometria. Use e-mail e senha.");
    } finally {
      setLoading(false);
    }
  }

  function formatarCelular(v) {
    const digits = v.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0,2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7)}`;
  }

  async function handleRegister(e) {
    e.preventDefault();
    setError("");
    if (!nome.trim()) { setError("Preencha seu nome."); return; }
    if (!email.trim()) { setError("Preencha seu e-mail."); return; }
    if (password.length < 6) { setError("A senha deve ter pelo menos 6 caracteres."); return; }
    if (password !== confirmPassword) { setError("As senhas não coincidem."); return; }
    setLoading(true);
    try {
      const celularLimpo = celular.replace(/\D/g, "");
      const { error: authError } = await supabase.auth.signUp({ email: email.trim(), password: password.trim(), options: { data: { nome: nome.trim(), celular: celularLimpo || null } } });
      if (authError) {
        if (authError.message.includes("already registered")) setError("Este e-mail já está cadastrado. Faça login.");
        else setError("Erro ao cadastrar: " + authError.message);
      } else {
        setMode("success");
        onCadastroSucesso?.(nome.trim().split(" ")[0]);
      }
    } catch (err) {
      setError("Erro inesperado. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }

  async function handleForgot(e) {
    e.preventDefault();
    setError("");
    if (!email.trim()) { setError("Preencha seu e-mail."); return; }
    setLoading(true);
    try {
      let emailNaoExiste = false;
      try {
        const check = await fetch("https://authentic-trust-production-0edc.up.railway.app/verificar-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim() }),
          signal: AbortSignal.timeout(5000),
        });
        if (check.ok) {
          const json = await check.json();
          if (json.existe === false) emailNaoExiste = true;
        }
      } catch (_) {}

      if (emailNaoExiste) {
        setError("Este e-mail não está cadastrado. Crie sua conta para começar! 👇");
        setLoading(false);
        return;
      }

      const { error: authError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: "https://mapafinanceiro5g.com.br/auth/callback",
      });
      if (authError) setError("Erro ao enviar e-mail: " + authError.message);
      else setMode("forgotSent");
    } catch (err) {
      setError("Erro inesperado. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    setError("");
    setLoading(true);
    const { error: googleError } = await signInWithGoogle();
    if (googleError) setError(googleError);
    setLoading(false);
  }

  function switchMode(m) {
    setMode(m); setError(""); setEmail(""); setPassword(""); setConfirmPassword(""); setNome("");
    setShowPassword(false); setShowConfirmPassword(false);
  }

  if (mode === "success") {
    return (
      <div style={s.root}><div style={s.card}>
        <div style={{...s.logo,background:"linear-gradient(135deg,#22c55e,#16a34a)"}}>✅</div>
        <h1 style={s.title}>Cadastro realizado!</h1>
        <p style={{...s.subtitle,margin:"0 0 24px",lineHeight:1.6}}>Conta criada com sucesso!</p>
        <button onClick={() => switchMode("login")} style={s.button}>Ir para o Login</button>
      </div></div>
    );
  }

  if (mode === "forgotSent") {
    return (
      <div style={s.root}><div style={s.card}>
        <div style={{...s.logo,background:"linear-gradient(135deg,#f59e0b,#d97706)"}}>📬</div>
        <h1 style={s.title}>E-mail enviado!</h1>
        <p style={{...s.subtitle,margin:"0 0 16px",lineHeight:1.6}}>
          Enviamos as instruções para <strong style={{color:"#a5b4fc"}}>{email}</strong>.
        </p>
        <div style={s.infoBox}>
          <p style={{margin:0,fontSize:13,color:"#94a3b8",lineHeight:1.6}}>
            📬 Não encontrou? Verifique <strong style={{color:"#e2e8f0"}}>Spam</strong> ou <strong style={{color:"#e2e8f0"}}>Promoções</strong>.<br/>
            O link expira em <strong style={{color:"#e2e8f0"}}>1 hora</strong>.
          </p>
        </div>
        <button onClick={() => switchMode("login")} style={{...s.button,marginTop:16}}>Voltar ao Login</button>
      </div></div>
    );
  }

  if (mode === "forgot") {
    return (
      <div style={s.root}><div style={s.card}>
        <div style={{...s.logo,background:"linear-gradient(135deg,#7c83ff,#9b6bff)"}}>🔑</div>
        <h1 style={s.title}>Recuperar senha</h1>
        <p style={{...s.subtitle,margin:"0 0 24px"}}>Digite seu e-mail e enviaremos um link para criar uma nova senha.</p>
        <form onSubmit={handleForgot} style={s.form}>
          <div style={s.field}>
            <label style={s.label}>E-mail</label>
            <input style={s.input} type="email" placeholder="seu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" disabled={loading} autoFocus/>
          </div>
          {error && (
            <div>
              <div style={s.errorBox}>{error}</div>
              {error.includes("não está cadastrado") && (
                <button type="button" onClick={() => switchMode("register")}
                  style={{...s.button,marginTop:8,background:"linear-gradient(135deg,#22c55e,#16a34a)",boxShadow:"0 8px 24px rgba(34,197,94,0.3)"}}>
                  ✨ Criar minha conta grátis
                </button>
              )}
            </div>
          )}
          <button type="submit" style={loading ? {...s.button,...s.buttonDisabled} : s.button} disabled={loading}>
            {loading ? "Enviando..." : "Enviar link de recuperação"}
          </button>
        </form>
        <div style={s.switchRow}>
          <button onClick={() => switchMode("login")} style={s.switchBtn}>← Voltar ao login</button>
        </div>
      </div></div>
    );
  }

  if (mode === "register") {
    return (
      <div style={s.root}><div style={s.card}>
        <div style={s.logoWrap}><img src={porquinho} alt="Porquinho Ninja" style={s.logoImg}/></div>
        <h1 style={s.title}>Criar conta</h1>
        <p style={s.subtitle}>Organize suas finanças gratuitamente</p>
        {GOOGLE_LOGIN_ENABLED && (<>
        <button type="button" onClick={handleGoogle} disabled={loading} style={{
          width:"100%",padding:"13px",borderRadius:14,
          background:"#fff",color:"#1f1f1f",
          fontWeight:700,fontSize:15,
          border:"none",cursor:"pointer",fontFamily:"inherit",
          boxShadow:"0 2px 12px rgba(0,0,0,0.35)",
          display:"flex",alignItems:"center",justifyContent:"center",gap:10,
          marginBottom:6,
          opacity: loading ? 0.6 : 1,
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Cadastrar com Google
        </button>
        <p style={{textAlign:"center",color:"#52526a",fontSize:11,margin:"-2px 0 10px"}}>
          Mais rápido · sem precisar criar senha
        </p>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
          <div style={{flex:1,height:1,background:"#2a2a3e"}}/>
          <span style={{color:"#52526a",fontSize:12}}>ou cadastre com e-mail</span>
          <div style={{flex:1,height:1,background:"#2a2a3e"}}/>
        </div>
        </>)}
        <form onSubmit={handleRegister} style={s.form}>
          <div style={s.field}><label style={s.label}>Nome</label><input style={s.input} type="text" placeholder="Seu nome completo" value={nome} onChange={(e) => setNome(e.target.value)} disabled={loading}/></div>
          <div style={s.field}><label style={s.label}>E-mail</label><input style={s.input} type="email" placeholder="seu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" disabled={loading}/></div>
          <div style={s.field}>
            <label style={s.label}>Senha</label>
            <div style={{position:"relative"}}>
              <input style={{...s.input,paddingRight:48,width:"100%",boxSizing:"border-box"}} type={showPassword?"text":"password"} placeholder="Mínimo 6 caracteres" value={password} onChange={e=>setPassword(e.target.value)} autoComplete="new-password" disabled={loading}/>
              <button type="button" tabIndex={-1} onMouseDown={e=>e.preventDefault()} onClick={()=>setShowPassword(v=>!v)} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:"#8f8fa4",padding:4,display:"flex"}}>
                {showPassword?<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
              </button>
            </div>
          </div>
          <div style={s.field}>
            <label style={s.label}>Confirmar senha</label>
            <div style={{position:"relative"}}>
              <input style={{...s.input,paddingRight:48,width:"100%",boxSizing:"border-box"}} type={showConfirmPassword?"text":"password"} placeholder="Repita a senha" value={confirmPassword} onChange={e=>setConfirmPassword(e.target.value)} autoComplete="new-password" disabled={loading}/>
              <button type="button" tabIndex={-1} onMouseDown={e=>e.preventDefault()} onClick={()=>setShowConfirmPassword(v=>!v)} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:"#8f8fa4",padding:4,display:"flex"}}>
                {showConfirmPassword?<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
              </button>
            </div>
          </div>
          <div style={s.field}>
            <label style={s.label}>WhatsApp <span style={{color:"#52526a",fontWeight:400,fontSize:12}}>(opcional)</span></label>
            <input style={s.input} type="tel" placeholder="(00) 00000-0000" value={celular} onChange={e => setCelular(formatarCelular(e.target.value))} inputMode="numeric" disabled={loading}/>
            <span style={{fontSize:11,color:"#52526a",marginTop:-4}}>📲 Para receber dicas e alertas no WhatsApp</span>
          </div>
          {error && <div style={s.errorBox}>{error}</div>}
          <button type="submit" style={loading ? {...s.button,...s.buttonDisabled} : s.button} disabled={loading}>
            {loading ? "Cadastrando..." : "Criar minha conta"}
          </button>
        </form>
        <div style={s.switchRow}>
          <span style={s.switchText}>Já tem conta?</span>
          <button onClick={() => switchMode("login")} style={s.switchBtn}>Fazer login</button>
        </div>
        <div style={s.infoBox}><p style={{margin:0,fontSize:12,color:"#64748b",lineHeight:1.5}}>🔒 Seus dados são armazenados com segurança e nunca compartilhados.</p></div>
      </div></div>
    );
  }

  // Tela login
  return (
    <div style={s.root}>
      <div style={s.card}>

        {/* Porquinho no carro animado */}
        <div style={{textAlign:"center",marginBottom:8,marginTop:-8}}>
          <div style={{
            display:"inline-block",
            animation:"carDrive 2s ease-in-out infinite",
            filter:"drop-shadow(0 12px 32px rgba(245,158,11,0.45))",
            position:"relative",
          }}>
            <img src={porquinhoCarro} alt="Porquinho no carro" style={{width:140,height:140,objectFit:"contain"}}/>
            {/* Rodas girando */}
            <div style={{position:"absolute",bottom:14,left:22,width:18,height:18,borderRadius:"50%",border:"3px solid #f59e0b",animation:"wheelSpin 0.5s linear infinite"}}/>
            <div style={{position:"absolute",bottom:14,right:18,width:18,height:18,borderRadius:"50%",border:"3px solid #f59e0b",animation:"wheelSpin 0.5s linear infinite"}}/>
            {/* Faróis */}
            <div style={{position:"absolute",bottom:22,left:4,width:8,height:4,borderRadius:2,background:"#fbbf24",boxShadow:"0 0 10px #fbbf24",animation:"headlight 1.5s ease-in-out infinite"}}/>
          </div>
        </div>

        <h1 style={{...s.title,marginBottom:2}}>Mapa Financeiro 5G</h1>
        <p style={{...s.subtitle,marginBottom:16}}>Sua jornada rumo à liberdade financeira 🚀</p>

        {/* CTA principal — 1 toque via Google, já cobre cadastro E login (signInWithIdToken cria a conta se não existir) */}
        {GOOGLE_LOGIN_ENABLED && (
        <button type="button" onClick={handleGoogle} disabled={loading} style={{
          width:"100%",padding:"15px",borderRadius:14,
          background:"#fff",color:"#1f1f1f",
          fontWeight:800,fontSize:15,
          border:"none",cursor:"pointer",fontFamily:"inherit",
          boxShadow:"0 8px 28px rgba(0,0,0,0.35)",
          display:"flex",alignItems:"center",justifyContent:"center",gap:10,
          marginBottom:6,
          animation:"ctaGlow 2.5s ease-in-out infinite",
          opacity: loading ? 0.6 : 1,
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          {loading ? "Entrando..." : "✨ Continuar com Google"}
        </button>
        )}
        <p style={{textAlign:"center",color:"#52526a",fontSize:11,margin:"0 0 6px"}}>
          Plano gratuito para sempre · Sem cartão de crédito
        </p>
        <p style={{textAlign:"center",margin:"0 0 14px"}}>
          <button type="button" onClick={() => switchMode("register")} style={{...s.switchBtn,fontSize:13}}>
            ou criar conta com e-mail
          </button>
        </p>

        {/* Separador */}
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
          <div style={{flex:1,height:1,background:"#2a2a3e"}}/>
          <span style={{color:"#52526a",fontSize:12}}>já tenho conta com e-mail</span>
          <div style={{flex:1,height:1,background:"#2a2a3e"}}/>
        </div>

        {/* Biometria — destaque se disponível */}
        {biometriaDisponivel && (
          <button type="button" onClick={handleBiometria} disabled={loading}
            style={{...s.button, marginBottom:10, background:"linear-gradient(135deg,#f59e0b,#d97706)", boxShadow:"0 6px 20px rgba(245,158,11,0.35)", display:"flex", alignItems:"center", justifyContent:"center", gap:10, fontSize:14, padding:"13px"}}>
            <span style={{fontSize:20}}>🖐️</span>
            <span>{biometriaEmail ? `Entrar como ${biometriaEmail.split("@")[0]}` : "Entrar com biometria"}</span>
          </button>
        )}

        {/* Form login */}
        <form onSubmit={handleLogin} style={s.form}>
          <div style={s.field}>
            <label style={s.label}>E-mail</label>
            <input style={s.input} type="email" placeholder="seu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" disabled={loading}/>
          </div>
          <div style={s.field}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <label style={s.label}>Senha</label>
              <button type="button" onClick={() => switchMode("forgot")} style={{...s.switchBtn,fontSize:12}}>Esqueci minha senha</button>
            </div>
            <div style={{position:"relative"}}>
              <input style={{...s.input,paddingRight:48,width:"100%",boxSizing:"border-box"}} type={showPassword?"text":"password"} placeholder="••••••••" value={password} onChange={e=>setPassword(e.target.value)} autoComplete="current-password" disabled={loading}/>
              <button type="button" tabIndex={-1} onMouseDown={e=>e.preventDefault()} onClick={()=>setShowPassword(v=>!v)} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:"#8f8fa4",padding:4,display:"flex"}}>
                {showPassword?<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
              </button>
            </div>
          </div>
          {error && <div style={s.errorBox}>{error}</div>}
          <button type="submit" style={loading ? {...s.button,...s.buttonDisabled} : s.button} disabled={loading}>
            {loading ? "Entrando..." : "Entrar"}
          </button>
        </form>

        
      </div>

      <style>{`
        @keyframes carDrive {
          0%,100%{transform:translateY(0) rotate(-0.5deg)}
          25%{transform:translateY(-4px) rotate(0.5deg)}
          75%{transform:translateY(-2px) rotate(-0.3deg)}
        }
        @keyframes wheelSpin {
          from{transform:rotate(0deg)} to{transform:rotate(360deg)}
        }
        @keyframes headlight {
          0%,100%{opacity:0.7;box-shadow:0 0 8px #fbbf24}
          50%{opacity:1;box-shadow:0 0 18px #fbbf24}
        }
        @keyframes ctaGlow {
          0%,100%{box-shadow:0 8px 28px rgba(16,185,129,0.4)}
          50%{box-shadow:0 8px 36px rgba(16,185,129,0.65)}
        }
      `}</style>
    </div>
  );
}

const s = {
  root:{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"radial-gradient(circle at top left,rgba(124,131,255,0.18),transparent 28%),radial-gradient(circle at bottom right,rgba(168,85,247,0.14),transparent 24%),#09090f",padding:"20px",fontFamily:"Inter,ui-sans-serif,system-ui,sans-serif"},
  card:{background:"linear-gradient(180deg,#13131f,#0f0f1a)",border:"1px solid #2a2a3e",borderRadius:"28px",padding:"40px 36px",width:"100%",maxWidth:"420px",boxShadow:"0 40px 100px rgba(0,0,0,0.5)",textAlign:"center"},
  logo:{width:"72px",height:"72px",display:"flex",alignItems:"center",justifyContent:"center",background:"linear-gradient(135deg,#7c83ff,#9b6bff)",borderRadius:"22px",fontSize:"32px",margin:"0 auto 20px",boxShadow:"0 12px 30px rgba(124,131,255,0.3)"},
  logoWrap:{width:"96px",height:"96px",margin:"0 auto 20px",filter:"drop-shadow(0 8px 24px rgba(124,131,255,0.4))"},
  logoImg:{width:"96px",height:"96px",objectFit:"contain"},
  title:{color:"#f8fafc",fontSize:"26px",fontWeight:900,margin:"0 0 8px",letterSpacing:"-0.02em"},
  subtitle:{color:"#8f8fa4",fontSize:"14px",margin:"0 0 32px"},
  form:{display:"flex",flexDirection:"column",gap:"16px",textAlign:"left"},
  field:{display:"flex",flexDirection:"column",gap:"8px"},
  label:{color:"#d4d4d8",fontSize:"14px",fontWeight:600},
  input:{background:"#12121d",color:"#f4f4f5",border:"1px solid #2b2b3d",borderRadius:"14px",padding:"14px 16px",fontSize:"15px",outline:"none",fontFamily:"inherit"},
  errorBox:{background:"#4c1118",border:"1px solid #dc2626",borderRadius:"12px",color:"#fecaca",padding:"12px 14px",fontSize:"14px"},
  infoBox:{background:"#0f1729",border:"1px solid #2a2a3e",borderRadius:12,padding:"12px 14px",marginTop:16,textAlign:"left"},
  button:{background:"linear-gradient(135deg,#6f6ff7,#9b6bff)",color:"#fff",border:"none",borderRadius:"14px",padding:"15px",fontSize:"16px",fontWeight:700,cursor:"pointer",marginTop:"8px",boxShadow:"0 8px 24px rgba(111,111,247,0.3)",fontFamily:"inherit",width:"100%"},
  buttonDisabled:{opacity:0.6,cursor:"not-allowed"},
  switchRow:{display:"flex",alignItems:"center",justifyContent:"center",gap:"8px",marginTop:"24px"},
  switchText:{color:"#52526a",fontSize:"14px"},
  switchBtn:{background:"none",border:"none",color:"#7c83ff",fontSize:"14px",fontWeight:700,cursor:"pointer",fontFamily:"inherit",padding:0,textDecoration:"underline"},
  footer:{color:"#52526a",fontSize:"13px",marginTop:"16px",marginBottom:0},
};
